import dayjs from "dayjs"
import useMutationHook from "../../hooks/useCustomMutation"
import { IShift, IShiftJob } from "../users/interface"
import { UPDATE_JOB_STATUS_MUTATION, BULK_UPDATE_JOBS } from "./graphql/mutations" // Adjust the path as needed
import { EJobStatus, IJob, IJobItems, IJobListing, IShiftJobs, IShiftStopIds } from "./interface"

const jobStatuses = [
    EJobStatus.idle,
    EJobStatus.onRouteToPickup,
    EJobStatus.atPickupLocation,
    EJobStatus.loading,
    EJobStatus.loaded,
    EJobStatus.onRouteToDelivery,
    EJobStatus.atDeliveryLocation,
    EJobStatus.unloading,
    EJobStatus.unloaded,
    EJobStatus.completed,
]

export const jobInPickupPhase = (jobStatus: EJobStatus): boolean => {
    return jobStatuses.indexOf(jobStatus) <= jobStatuses.indexOf(EJobStatus.loaded)
}

export const jobInDeliveryPhase = (jobStatus: EJobStatus): boolean => {
    return (
        jobStatuses.indexOf(jobStatus) <= jobStatuses.indexOf(EJobStatus.completed) &&
        jobStatuses.indexOf(jobStatus) > jobStatuses.indexOf(EJobStatus.loaded)
    )
}

export const useUpdateStatusHook = () => {
    // Pass options as needed (e.g., headers, error handling, etc.)
    //@ts-ignore
    const [enhancedMutate, { data, error, loading: jobUpdateMutationLoading }] = useMutationHook(
        UPDATE_JOB_STATUS_MUTATION,
        {}
    ) // Provide an empty object for options

    const handleJobUpdate = async (input: object, jobId: string) => {
        try {
            //@ts-ignore
            const response = await enhancedMutate({ id: jobId, record: input })
            return response // Return the response if needed
        } catch (error) {
            console.error("Error updating job status:", error)
            throw error // Rethrow the error if you want to handle it outside the hook
        }
    }

    return { handleJobUpdate, data, error, jobUpdateMutationLoading }
}
export const useBulkUpdateStatusHook = () => {
    // Pass options as needed (e.g., headers, error handling, etc.)
    //@ts-ignore
    const [enhancedMutate, { data, error, loading }] = useMutationHook(BULK_UPDATE_JOBS, {}) // Provide an empty object for options

    const handleBulkJobsUpdate = async (jobIds: string[], status: string | null) => {
        try {
            //@ts-ignore
            const response = await enhancedMutate({ jobIds: jobIds, jobStatus: status })
            return response // Return the response if needed
        } catch (error) {
            console.error("Error updating job status:", error)
            throw error // Rethrow the error if you want to handle it outside the hook
        }
    }

    return { handleBulkJobsUpdate, data, error, loading }
}

export const getNextJobStatus = (currentStatus: EJobStatus): string | null => {
    const currentIndex = jobStatuses.indexOf(currentStatus)

    if (currentIndex === -1) {
        throw new Error(`Invalid job status: ${currentStatus}`)
    }

    // Return the next status, or null if it's the last status
    return jobStatuses[currentIndex + 1] || null
}

export const getPreviousJobStatus = (currentStatus: EJobStatus): EJobStatus => {
    const currentIndex = jobStatuses.indexOf(currentStatus)

    if (currentIndex === -1) {
        throw new Error(`Invalid job status: ${currentStatus}`)
    }

    // Return the previous status, or null if it's the first status
    return jobStatuses[currentIndex - 1] || EJobStatus.idle
}

/**
 * Add a job to a list of stops for a group job. The stops are grouped by the destination address. So if we have a group job
 * with stops [Pickup from A, Pickup From A, Pickup From B, Delivery to C, Delivery to D, Delivery to D], the stops will be
 * grouped as [[Pickup from A, Pickup from A], [Pickup from B], [Delivery to C], [Delivery to D, Delivery to D]]
 *
 * @param stopsInGroup to list of stops for the group
 * @param jobData the data of the job
 * @param job the grouping data of the job
 */
function addJobToGroup(stopsInGroup: IJobListing[][], jobData: IJob, job: IShiftJob) {
    const jobToAdd = {
        ...jobData,
        type: job.grouping.type === "splitLoad" ? "pickup" : "delivery",
    }

    if (stopsInGroup.length === 0) {
        //Currently no stops for group
        stopsInGroup.push([jobToAdd])
    } else {
        const latestStopList = stopsInGroup[stopsInGroup.length - 1]
        const latestStop = latestStopList?.[0]

        if (latestStop.type === jobToAdd.type) {
            if (jobToAdd.type === "pickup" && latestStop.originAddress._id === jobToAdd.originAddress._id) {
                latestStopList.push(jobToAdd)
            } else if (
                jobToAdd.type === "delivery" &&
                latestStop.destinationAddress._id === jobToAdd.destinationAddress._id
            ) {
                latestStopList.push(jobToAdd)
            } else {
                stopsInGroup.push([jobToAdd])
            }
        } else {
            stopsInGroup.push([jobToAdd])
        }
    }
}

export const splitJobIntoPickupAndDelivery = (shift: IShift, records: IJob[]) => {
    const validJobIds = new Set(
        records.map((record: IJob) => record._id) // Extract IDs
    )

    const stopsByGroupId: IShiftJobs = {}

    shift?.jobs?.forEach((job: IShiftJob) => {
        if (validJobIds.has(job.jobId)) {
            // Find the corresponding record from the records array based on jobId
            const record = records.find((record: IJob) => record._id === job.jobId)
            if (record) {
                // Create a fake 'group' for each job that doesn't have a grouping
                const key = job.grouping?.groupId || job.jobId

                // Ensure the key exists as an array
                if (!stopsByGroupId[key]) {
                    stopsByGroupId[key] = { groupJob: !!job.grouping, destinationStops: [] }
                }

                const group = stopsByGroupId[key]

                if (job.grouping) {
                    addJobToGroup(group.destinationStops, record, job)
                } else {
                    // Add the job twice for splitLoad and splitUnload when grouping is null or undefined
                    group.destinationStops.push([
                        {
                            ...record,
                            type: "pickup",
                        },
                    ])

                    group.destinationStops.push([
                        {
                            ...record,
                            type: "delivery",
                        },
                    ])
                }
            }
        }
    })

    const stopIdsByGroupId: IShiftStopIds = {}
    Object.keys(stopsByGroupId).map((groupId) => {
        const stops = stopsByGroupId[groupId].destinationStops.map((stops) =>
            stops.map((stop) => {
                return { jobId: stop._id, type: stop.type, status: stop.jobStatus }
            })
        )
        stopIdsByGroupId[groupId] = {
            groupJob: stopsByGroupId[groupId].groupJob,
            destinationStops: stops,
        }
    })

    return {
        ids: stopIdsByGroupId,
        jobs: stopsByGroupId,
    }
}

export const getDocumentType = (status: string, index: number): string => {
    if (status === "pickup") {
        return index === 0 ? "POP_Signature" : "POP_Image"
    } else if (status === "delivery") {
        return index === 0 ? "POD_Signature" : "POD_Image"
    }
    return ""
}
export const getDescriptionByStatus = (status: string): string => {
    return status === "pickup"
        ? "Proof of Pickup Documents"
        : status === "delivery"
          ? "Proof of Delivery Documents"
          : ""
}

export const createFileFromBase64 = (base64: string, mimeType: string): File => {
    const binary = atob(base64.split(",")[1])
    const array = Uint8Array.from(binary, (char) => char.charCodeAt(0))
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-")
    // Extract the file extension from the MIME type
    const extension = mimeType.split("/")[1] || "bin"
    const fileName = `file_${timestamp}.${extension}`

    return new File([array], fileName, { type: mimeType, lastModified: Date.now() })
}
export const uploadFileToS3 = (uploadUrl: string, file: File) => {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.onload = () => {
            if (xhr.status === 200) {
                resolve(xhr.response)
            } else {
                reject(new Error(`Upload failed with status: ${xhr.status}`))
            }
        }
        xhr.onerror = () => reject(new Error("Upload failed due to a network error"))

        xhr.open("PUT", uploadUrl)
        xhr.setRequestHeader("Content-Type", file.type)
        xhr.send(file)
    })
}

export const formatDateTime = (dateTime: string): string => {
    if (dateTime && dateTime.includes("T")) {
        const date = dayjs(dateTime)
        if (date.isValid()) {
            // Format date-time string into 'DD MMM YYYY, HH:mm'
            return date.format("DD MMM YYYY, HH:mm")
        }
    }
    return dateTime // Return as is if not a valid date-time string
}

/**
 * Returns the file extension based on the provided MIME type.
 * This utility function checks the MIME type string and maps it to a corresponding file extension.
 *
 * @param {string} fileType - The MIME type of the file (e.g., "image/jpeg", "application/pdf").
 * @returns {string} - The corresponding file extension (e.g., "image", "pdf", "docx", "xlsx", "pptx").
 *
 * Usage:
 * getFileExtension("image/jpeg") // returns "image"
 * getFileExtension("application/pdf") // returns "pdf"
 */
export const getFileExtension = (fileType: string): string => {
    if (fileType.includes("image")) return "image"
    if (fileType.includes("pdf")) return "pdf"
    if (fileType.includes("msword") || fileType.includes("officedocument.wordprocessingml.document")) return "docx"
    if (fileType.includes("vnd.ms-excel") || fileType.includes("spreadsheetml.sheet")) return "xlsx"
    if (fileType.includes("presentation") || fileType.includes("powerpoint")) return "pptx"
    return "file" // fallback
}

export const clearSessionStorage = (keys: string[]) => {
    keys.forEach((key) => window.sessionStorage.removeItem(key))
}

export const getCompletedJobGroups = (): Record<string, string[]> => {
    const storedCompletedJobGroups = localStorage.getItem("completedJobGroups")
    return storedCompletedJobGroups ? JSON.parse(storedCompletedJobGroups) : {}
}

export const setCompletedJobGroups = (completedJobGroups: Record<string, string[]>): void => {
    localStorage.setItem("completedJobGroups", JSON.stringify(completedJobGroups))
}

export const revertJobsQuantity = async (
    preJobStatus: string,
    jobs: (IJob | null)[],
    handleJobUpdate: (input: object, jobId: string) => Promise<any>
) => {
    const updatedJobs = []

    for (const [jobIndex, job] of jobs.entries()) {
        if (!job) return
        try {
            const payload = {
                items:
                    job.items?.map((item: IJobItems) => ({
                        itemId: item.itemId,
                        quantity: item?.quantity,
                        quantityLoaded: preJobStatus === EJobStatus.atPickupLocation ? null : item.quantityLoaded,
                        quantityUnloaded: null,
                    })) || [],
            }

            const items = job.items?.map((item: IJobItems) => ({
                ...item,
                itemId: item.itemId,
                quantity: item.quantity,
                quantityLoaded: preJobStatus === EJobStatus.atPickupLocation ? null : item.quantityLoaded,
                quantityUnloaded: null,
            }))

            await handleJobUpdate(payload, job._id)

            const updatedJob = {
                ...job,
                items: items,
            }

            updatedJobs.push(updatedJob)
        } catch (jobError) {
            console.error(`Error updating job at index ${jobIndex}:`, jobError)
        }
    }
    return updatedJobs
}
